import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ArrowRight, Inbox } from 'lucide-react';

const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700',
  ready_for_review: 'bg-yellow-100 text-yellow-700',
  under_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  submitted: 'bg-purple-100 text-purple-700',
  rejected: 'bg-red-100 text-red-700',
  changes_requested: 'bg-orange-100 text-orange-700',
  submission_failed: 'bg-orange-100 text-orange-700',
};

const formatRelativeDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const formatStatus = (status) => {
  return (status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const DashboardRecentProposals = ({ proposals, loading }) => {
  const navigate = useNavigate();

  const getProposalActivityDate = (proposal) => {
    if (proposal?.source === 'upwork') {
      return proposal.proposalSentAt || proposal.submittedAt || proposal.createdAt;
    }
    return proposal.submittedAt || proposal.createdAt;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        <div className="divide-y divide-gray-100">
          {[1, 2, 3].map(i => (
            <div key={i} className="p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!proposals?.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-4">
          <div className="mx-auto w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <Inbox className="h-5 w-5 text-gray-400" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">No proposals yet</h3>
          <p className="text-sm text-gray-500 mb-3">Create your first proposal to get started.</p>
          <button
            onClick={() => navigate('/jobs')}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
          >
            Find Work
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Recent Proposals</h3>
        <button
          onClick={() => navigate('/proposals')}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
        >
          View all <ArrowRight className="h-3 w-3" />
        </button>
      </div>
      <div className="divide-y divide-gray-100">
        {proposals.map(proposal => (
          <button
            key={proposal._id}
            onClick={() => navigate(`/proposals/${proposal._id}`)}
            className="w-full p-4 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
          >
            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {proposal.jobPostingId?.title || proposal.title || 'Untitled Proposal'}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${STATUS_BADGE[proposal.status] || 'bg-gray-100 text-gray-700'}`}>
                  {formatStatus(proposal.status)}
                </span>
                <span className="text-xs text-gray-500">
                  {formatRelativeDate(getProposalActivityDate(proposal))}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default DashboardRecentProposals;
